import { ORIENTATION } from '../../../hooks/useImageOrientation';

/**
 * WHICH OF A STUDENT'S PHOTOS A GIVEN PRODUCT CAN ACTUALLY HOLD.
 *
 * Two surfaces let a customer pick the pose that goes on an add-on -- the
 * MultiPose builder ("Sheet 1 of 1: …") and the gifts product screen -- and
 * they must offer the same set, or a pose rejected on one is carried in from
 * the other. This is that rule, pure and in one place, same reasoning as
 * `visiblePoses`.
 *
 * ⚠️ THE CONSTRAINT IS REAL, NOT COSMETIC. `weeklycutout` stands the student on
 * the artwork as a transparent 4:5 cutout with no frame around it:
 *   - a LANDSCAPE pose overflows the slot (a slot sets width and lets height
 *     follow the pose's ratio -- see the gift-placement notes);
 *   - a JPG pose has its backdrop burned in by EC2, so it lands as a rectangle
 *     of studio canvas sitting on a photo of a fridge.
 *
 * Both answers default to "allowed", because that is what every product did
 * before this rule existed.
 *
 * The answers are DERIVED SERVER-SIDE FROM THE PHOTO PLACEMENT
 * (`Addon::POSE_RULES_BY_CSS_NAME`), not set per add-on, so every product drawn
 * by a given renderer inherits one answer -- including products nobody has
 * created yet. This module does not care where they came from; it only has to
 * keep treating `undefined` as "no opinion" rather than as "no".
 */

// A product may refuse landscape. Blank/undefined means it accepts anything,
// which is what the pose carousel did for every product until now.
export function acceptsLandscape(addon) {
  return addon?.allowsLandscapePoses !== false;
}

/**
 * A product may refuse JPGs. TRI-STATE, and the blank state is the interesting
 * one: it defers to `derivedBlocked`, which MultiPose computes from the Order
 * template (no element whose `src` mentions a background => a JPG cannot work).
 * That derivation is kept rather than replaced so no existing row changes, but
 * it silently answers "allowed" for an add-on with NO Order template -- which is
 * exactly how a JPG reached the weekly magnet. Setting the column overrules it.
 */
export function acceptsJpg(addon, derivedBlocked = false) {
  const explicit = addon?.allowsJpgPoses;

  if (explicit === true || explicit === false) return explicit;

  return !derivedBlocked;
}

// `extension` is the pose's own field; the MultiPose builder already reads it
// for `isAllJpg`. Production holds exactly two real values, `.png` (8.5M rows)
// and `.jpg` (5.0M) -- no `.jpeg`, no uppercase.
//
// ⚠️ BUT A THIRD of a percent CARRY NEITHER. ~31k poses have no extension at
// all, a few hundred hold junk left by a filename parse that went wrong
// (`._torres_0_524040-12`, `.7-01`). Asking only "does it say png" would call
// every one of those a JPG and drop a student's real photo off the magnet.
// A RECOGNISED extension decides; anything else asks the url instead of
// guessing, which is the same thing PreviewImage does.
function isJpg(pose) {
  const extension = (pose?.extension || '').toLowerCase();

  if (extension.includes('png')) return false;
  if (extension.includes('jpg') || extension.includes('jpeg')) return true;

  return !(pose?.watermarkUrl || pose?.imageUrl || '').toLowerCase().includes('.png');
}

/**
 * @param poses        the poses on offer, already favourite-sorted
 * @param addon        the add-on this sheet is for (undefined for a package
 *                     sheet -- an 8x10 or wallets -- which has no such rule)
 * @param orientations map of url -> { orientation }, from useImageOrientations
 * @param derivedJpgBlocked  MultiPose's Order-template derivation; false where
 *                     there is nothing to derive from
 */
export default function eligiblePoses(poses, addon, orientations = {}, derivedJpgBlocked = false) {
  const all = poses || [];

  if (!addon) return all;

  const landscapeOk = acceptsLandscape(addon);
  const jpgOk = acceptsJpg(addon, derivedJpgBlocked);

  if (landscapeOk && jpgOk) return all;

  const kept = all.filter((pose) => {
    if (!jpgOk && isJpg(pose)) return false;

    if (!landscapeOk) {
      // ⚠️ MEASURED, never inferred from the filename. Orientation is season ×
      // crop and a pose NUMBER never tells you which -- reading `-01/-04` out of
      // the name is the bug useImageOrientation was introduced to fix. An
      // unmeasured pose (UNKNOWN, or still loading) is KEPT: dropping it would
      // empty the carousel on first paint and then refill it, and a pose
      // flickering out from under a parent's cursor is worse than one that
      // turns out not to fit.
      const measured = orientations[pose?.watermarkUrl || pose?.imageUrl]?.orientation;

      if (measured === ORIENTATION.LANDSCAPE) return false;
    }

    return true;
  });

  // ⚠️ AN EMPTY RESULT IS THE RIGHT ANSWER, NOT A FAILURE TO ROUTE AROUND.
  //
  // This used to fall back to `all` when nothing survived, on the theory that
  // a pose that fits badly beats a dead end. Production disproved it within the
  // hour (2026-10-15): an all-JPG student -- no transparent cut-outs at all,
  // common for elementary sessions and exactly what Builder's `isAllJpg`
  // models -- opened the weekly magnet and saw every one of her four JPG poses
  // still on offer, because the rule removed all four and this line handed
  // them straight back. A pose the design cannot hold is not an option, and
  // offering it anyway is the bug this module exists to fix.
  //
  // The CALLERS own the empty state: the builder says why there is nothing to
  // choose, and the shelf does not offer a product a student has no photo for
  // (freeMagnetsVisibility is the precedent). `visiblePoses` keeps its own
  // stale-favourites fallback -- that one restores poses the customer CAN use.
  return kept;
}
